/**
 * The "Network" tab: every address this account has connected from, and who else has.
 *
 * The second column is the reason the tab exists. Two accounts that keep turning up on the same
 * home address are worth a look; two that share a carrier address once are not, because a phone
 * network puts thousands of strangers behind one address. So the count is shown, the names are
 * linked, and nothing here calls anything a duplicate — that is the reader's call.
 *
 * Banning is offered per address. The dialog lets the rule be widened to a range before it is
 * placed, because the one address you can see is rarely the whole story.
 */

import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, Chip, Link, Stack, Tooltip, Typography } from '@mui/material'
import { Block, OpenInNew } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ipApi } from '@api/endpoints/ip'
import type { AdminUserDto } from '@api/types/admin'
import type { UserIpAddressDto } from '@api/types/ip'
import { IpBanDialog } from '../ip/IpBanDialog'
import { describeBanTerm, shortUserAgent } from '../ip/ipFormatting'
import { adminUserIpAddressesKey } from './userQueryKeys'
import { errorMessage, formatDate } from './userFormatting'

export interface UserNetworkPanelProps {
  user: AdminUserDto
}

export function UserNetworkPanel({ user }: UserNetworkPanelProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [banRule, setBanRule] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: adminUserIpAddressesKey(user.id),
    queryFn: () => ipApi.getUserAddresses(user.id),
  })

  const columns = useMemo<DataTableColumn<UserIpAddressDto>[]>(
    () => [
      {
        key: 'ipAddress',
        header: 'Address',
        primary: true,
        render: (row) => (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {row.ipAddress}
            </Typography>
            {row.activeBan && (
              <Tooltip title={`${row.activeBan.rule}: ${row.activeBan.reason}`}>
                <Chip size="small" color="error" icon={<Block />} label={`Banned ${describeBanTerm(row.activeBan)}`} />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        key: 'lastSeenAt',
        header: 'Last seen',
        render: (row) => (
          <Typography variant="body2">
            {formatDate(row.lastSeenAt)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({row.lastSource.toLowerCase()})
            </Typography>
          </Typography>
        ),
      },
      {
        key: 'firstSeenAt',
        header: 'First seen',
        hideBelow: 'md',
        render: (row) => formatDate(row.firstSeenAt),
      },
      {
        key: 'hitCount',
        header: 'Hits',
        align: 'right',
        hideBelow: 'md',
        render: (row) => row.hitCount,
      },
      {
        key: 'lastUserAgent',
        header: 'Client',
        hideBelow: 'lg',
        render: (row) => (
          <Tooltip title={row.lastUserAgent || 'Unknown client'}>
            <span>{shortUserAgent(row.lastUserAgent) || '—'}</span>
          </Tooltip>
        ),
      },
      {
        key: 'otherAccounts',
        header: 'Other accounts here',
        render: (row) =>
          row.otherAccountCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
              {row.otherAccounts.map((account) => (
                <Chip
                  key={account.userId}
                  size="small"
                  variant="outlined"
                  color={account.isActive ? 'default' : 'warning'}
                  component={RouterLink}
                  to={`/users/${account.userId}?tab=network`}
                  clickable
                  label={account.displayName || account.email}
                  title={`${account.email} · last here ${formatDate(account.lastSeenAt)}`}
                />
              ))}
              {row.otherAccountCount > row.otherAccounts.length && (
                <Link
                  component={RouterLink}
                  to={`/ip-bans?ip=${encodeURIComponent(row.ipAddress)}`}
                  variant="body2"
                >
                  +{row.otherAccountCount - row.otherAccounts.length} more
                </Link>
              )}
            </Stack>
          ),
      },
    ],
    [],
  )

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        A shared address is a lead, not proof. Carrier networks and households put unrelated
        accounts on one address every day. Ordinary requests are recorded at most once per
        address every fifteen minutes; sign-ins and refreshes always are.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}>
          {errorMessage(error, 'Failed to load this user’s addresses')}
        </Alert>
      )}

      <DataTable<UserIpAddressDto>
        rows={data ?? []}
        columns={columns}
        getRowKey={(row) => row.ipAddress}
        loading={isLoading}
        emptyMessage="No addresses recorded yet. History starts from the first sign-in after tracking was switched on."
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        rowActions={(row) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Look up this address">
              <Button
                size="small"
                component={RouterLink}
                to={`/ip-bans?ip=${encodeURIComponent(row.ipAddress)}`}
                startIcon={<OpenInNew />}
              >
                Look up
              </Button>
            </Tooltip>
            <Button
              size="small"
              color="error"
              startIcon={<Block />}
              disabled={Boolean(row.activeBan)}
              onClick={() => setBanRule(row.ipAddress)}
            >
              Ban
            </Button>
          </Stack>
        )}
      />

      <IpBanDialog
        open={banRule !== null}
        onClose={() => setBanRule(null)}
        initialRule={banRule ?? ''}
        subjectUserId={user.id}
        onBanned={() => refetch()}
      />
    </Box>
  )
}
